import { useEffect, useState } from "react";
import { Reveal } from "@/hooks/useReveal";
import { api, resolveImage } from "@/lib/api";
import { Quote } from "lucide-react";

const Testimonials = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/testimonials", {
          params: { featured: true, limit: 12 },
        });
        setRows(data);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Hide the section entirely when there's nothing to show
  if (!loading && rows.length === 0) return null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#fafafa] py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24 items-end">
          <div className="md:col-span-7">
            <Reveal>
              <p className="overline mb-6">Testimonials</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black">
                The people behind
                <em className="italic font-light"> the addresses</em>.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={200}>
              <p className="text-base md:text-lg text-black/60 leading-relaxed">
                Every transaction is a transition — a chapter ending, a chapter beginning.
                Here's what our clients had to say about working with Manzano.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-8">
          {rows.map((t, i) => (
            <Reveal key={t.id} delay={i * 120}>
              <article
                data-testid={`testimonial-${t.id}`}
                className="group h-full bg-white p-8 md:p-10 border border-black/5 flex flex-col"
              >
                <Quote
                  size={24}
                  className="text-[#c0c0c0] mb-6 shrink-0"
                  strokeWidth={1.25}
                />
                <p className="font-heading text-xl md:text-2xl leading-snug text-black/90 italic mb-10 flex-1">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-black/10">
                  {t.photo_url ? (
                    <img
                      src={resolveImage(t.photo_url)}
                      alt={t.client_name}
                      className="w-14 h-14 rounded-full object-cover bg-gray-100 grayscale"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center font-heading text-lg text-black/40">
                      {(t.client_name || "?").charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-black truncate">
                      {t.client_name || "Anonymous"}
                    </p>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-black/40 mt-0.5 truncate">
                      {[t.city, t.year].filter(Boolean).join(" · ")}
                    </p>
                    {(t.context || t.transaction_type) && (
                      <p className="text-xs text-black/60 mt-1 truncate">
                        {[t.transaction_type, t.context].filter(Boolean).join(" — ")}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
